import { Spin, Tooltip } from "antd";
import clsx from "clsx";
import PdfIcon from "@/shared/assets/svg/icons/pdf-icon.png";
import { DocumentFileName } from "@shared/ui";
import { resolveDocumentSource } from "../../../document";
import { useDocumentOpenAction } from "../../api";
import { buildDocumentFileLinkTooltip } from "./build-document-file-link-tooltip";
import "./document-file-link.scss";

export type DocumentFileLinkProps = {
  fileName: string;
  documentUrl?: string | null;
  fileId?: string | null;
  blobUrl?: string | null;
  localFile?: File | null;
  /** @deprecated use DocumentFileRow */
  showPdfIcon?: boolean;
  className?: string;
  /** Внешний вид текста: link — синяя ссылка, plain — обычный текст */
  variant?: "link" | "plain";
  /** Отключить открытие по клику на название (вид ссылки сохраняется) */
  disableLinkAction?: boolean;
  /** Дополнительные строки в подсказке при наведении на иконку */
  tooltipLines?: string[];
};

export function DocumentFileLink({
  fileName,
  documentUrl,
  fileId,
  blobUrl,
  localFile,
  showPdfIcon = false,
  className,
  variant = "link",
  disableLinkAction = false,
  tooltipLines,
}: DocumentFileLinkProps) {
  const { openDocument, isLoading, isFileLoading } = useDocumentOpenAction();

  const resolvedFileName =
    fileName || documentUrl?.split("/").pop() || "document.pdf";

  const hasLocalFile = Boolean(localFile && localFile.size > 0);
  const source = resolveDocumentSource({ documentUrl, fileId, blobUrl });
  const isOpenable = Boolean(source) || hasLocalFile;

  const loading =
    !disableLinkAction &&
    (isLoading || (fileId ? isFileLoading(fileId) : false));

  const tooltipContent = buildDocumentFileLinkTooltip({
    fileName: resolvedFileName,
    source: hasLocalFile ? { kind: "local", blobUrl: blobUrl || "" } : source,
    extraLines: tooltipLines,
    disableLinkAction,
  });

  const handleOpen = () => {
    if (disableLinkAction) {
      return;
    }

    const effectiveBlobUrl =
      blobUrl ||
      (hasLocalFile && localFile ? URL.createObjectURL(localFile) : undefined);

    openDocument({
      documentUrl,
      fileId,
      blobUrl: effectiveBlobUrl,
      fileName: resolvedFileName,
    });
  };

  if (!showPdfIcon && !fileName) {
    return null;
  }

  if (showPdfIcon) {
    if (!isOpenable) {
      return null;
    }

    return (
      <button
        type="button"
        className="document-file-link document-file-link--icon"
        onClick={(event) => {
          event.stopPropagation();
          handleOpen();
        }}
        disabled={loading}
        aria-label={`Просмотреть документ ${resolvedFileName}`}
      >
        {loading ? (
          <Spin size="small" />
        ) : (
          <Tooltip title={tooltipContent} placement="top">
            <img
              src={PdfIcon}
              alt="pdf"
              className="document-file-link__icon"
              width={16}
              height={16}
            />
          </Tooltip>
        )}
      </button>
    );
  }

  if (disableLinkAction && isOpenable) {
    return (
      <span
        className={clsx(
          "document-file-link",
          "document-file-link--with-name",
          "document-file-link--action-disabled",
          className,
        )}
        onClick={(event) => event.stopPropagation()}
      >
        <span
          className={clsx(
            "document-file-name",
            "document-file-name--clickable",
            variant === "plain" && "document-file-name--plain",
          )}
        >
          {fileName}
        </span>
      </span>
    );
  }

  return (
    <span
      className={clsx(
        "document-file-link",
        "document-file-link--with-name",
        className,
      )}
      onClick={(event) => event.stopPropagation()}
    >
      {loading ? (
        <Spin className="document-file-link__loading" size="small" />
      ) : (
        <>
          <DocumentFileName
            onClick={isOpenable ? handleOpen : undefined}
            variant={variant}
          >
            {fileName}
          </DocumentFileName>
        </>
      )}
    </span>
  );
}
